import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { confirmAlert } from 'react-confirm-alert';
import { bulkCancelPackages, getReservedAdminRows } from 'app/api/package';
import { isPackageRow } from 'app/components/PackageRow';
import Message from 'app/utils/Message';
import Toast from 'app/utils/Toast';
import { APP_CONSTANTS } from 'app/constants/app-constants';

// Unchanged wording for a voucher-only cancel; the bulk endpoint's own success
// text is package-worded, so it is not reused here.
const VOUCHER_CANCEL_SUCCESS = 'Vouchers Cancelled Successfully';

// One typed item per selected row. `type` is mandatory server-side and is the
// only thing that tells a package purchase id from a voucher reservation id.
const buildBulkItem = (row) =>
  isPackageRow(row)
    ? { type: 'package', purchaseId: row.purchaseId, customerId: row.customerId }
    : { type: 'voucher', id: row.id, customerId: row.customerId };

const rowKeyOfFailure = (failure) =>
  failure?.type === 'package'
    ? `package-${failure?.purchaseId}`
    : `voucher-${failure?.id}`;

const describeFailure = (failure, selectedRow) => {
  const isPackage = failure?.type === 'package';
  const kind = isPackage ? 'Bundle' : 'Voucher';
  const title = isPackage ? selectedRow?.title : selectedRow?.voucherTitle;
  const fallback = `#${isPackage ? failure?.purchaseId : failure?.id}`;
  const customer = selectedRow?.customerName
    ? ` (${selectedRow.customerName})`
    : '';
  return `${kind}: ${title || fallback}${customer}`;
};

const useCancelVoucher = () => {
  const navigate = useNavigate();
  const [pageCount, setPageCount] = useState(1);
  const [pageLoading, setPageLoading] = useState(false);
  const dataPerPage = 10;
  const [filteredData, setFilteredData] = useState([]);
  const [totalNumber, setTotalNumber] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  // keep active filters separately
  const [searchValue, setSearchValue] = useState('');
  const [filters, setFilters] = useState({
    startDate: null,
    endDate: null,
  });
  // Rows the last bulk cancel could not cancel, each with the server's reason.
  const [failedRows, setFailedRows] = useState([]);

  const isAnyChecked = filteredData.some((item) => item?.checked);
  const bottomButtons = [
    {
      customClassName: 'cancel_voucher_btn',
      title: APP_CONSTANTS.CANCEL_VOUCHER_TAG,
      handleClick: () => handleCancelBulkVoucher(),
      disabled: !isAnyChecked,
    },
    {
      customClassName: 'cancel_voucher_btn',
      title: APP_CONSTANTS.CLEAR_ALL,
      handleClick: () => handleCheckboxChange('all', false),
      disabled: !isAnyChecked,
    },
  ];

  function handleSubmit(event) {
    event.preventDefault();
    setCurrentPage(1); 
    setFilters(values); 
    fetchVoucherData(1, '', values); 
  }

  const [values, setValues] = useState({
    startDate: null,
    endDate: null,
  });

  const handleChange = (e) => {
    if (e?.target) {
      const { name, value } = e.target;
      setValues((prevValues) => ({
        ...prevValues,
        [name]: value,
      }));
    }
  };
  const sectionObj = {
    sectionTitle: 'Cancel Voucher Management',
    searchObj: {
      keys: ['voucherTitle', 'mallName', 'storeName'],
      placeholder: 'Search by Title, Mall name, Store Name',
      searchType: 'server',
      handleSearch: (val) => {
       setCurrentPage(1);
       setSearchValue(val); // ✅ save search term
       fetchVoucherData(1, val, filters); // ✅ refetch with filters + search
     },
    },
  };

  const fetchVoucherData = useCallback(
    async (page, search = searchValue, appliedFilters = filters) => {
      if (page === 1) {
        setCurrentPage(1);
      }
      setPageLoading(true);
      try {
        const response = await getReservedAdminRows({
          page,
          limit: dataPerPage,
          search,
          startDate: appliedFilters.startDate,
          endDate: appliedFilters.endDate,
        });

        if (response?.responseCode === 200) {
          const dataArr =
            response?.data?.data?.map((item) => ({
              ...item,
              checked: false,
            })) || [];

          setFilteredData(dataArr);

          // The mixed-row endpoint returns the grand total in totalItems;
          // its `count` is only the current page's length. Reading `count`
          // here collapsed the pager to a single page (CR-01).
          const totalCount =
            typeof response?.data?.totalItems === 'number'
              ? response.data.totalItems
              : typeof response?.data?.count === 'number'
                ? response.data.count
                : dataArr.length;

          setTotalNumber(totalCount);
          setPageCount(Math.ceil(totalCount / dataPerPage));
        } else {
          Toast.error(
            response?.response?.message || Message.Error.COMMON_MESSAGE
          );
        }
      } catch (error) {
        Toast.error(Message.Error.COMMON_MESSAGE);
      } finally {
        setPageLoading(false);
      }
    },
    [dataPerPage, filters, searchValue]
  );

  const sendBulkCancel = useCallback(
    async (selectedRows) => {
      setPageLoading(true);
      setFailedRows([]);
      try {
        const items = selectedRows.map(buildBulkItem);
        const response = await bulkCancelPackages(items);

        // A 400 where nothing was cancelled still carries data.failed, so the
        // per-row reasons are read from the response on both paths.
        const cancelledCount = Array.isArray(response?.data?.cancelled)
          ? response.data.cancelled.length
          : 0;
        const failures = Array.isArray(response?.data?.failed)
          ? response.data.failed
          : [];

        if (failures.length) {
          const rowsByKey = new Map(
            selectedRows.map((row) => [row.rowKey, row])
          );
          setFailedRows(
            failures.map((failure) => ({
              key: rowKeyOfFailure(failure),
              label: describeFailure(
                failure,
                rowsByKey.get(rowKeyOfFailure(failure))
              ),
              error: failure?.error || Message.Error.COMMON_MESSAGE,
            }))
          );
        }

        if (response?.responseCode === 200) {
          if (failures.length) {
            Toast.success(
              `${cancelledCount} cancelled. ${failures.length} could not be cancelled.`
            );
          } else if (selectedRows.some(isPackageRow)) {
            Toast.success(`${cancelledCount} cancelled successfully`);
          } else {
            Toast.success(VOUCHER_CANCEL_SUCCESS);
          }
        } else {
          Toast.error(
            response?.response?.message || Message.Error.COMMON_MESSAGE
          );
        }
        await fetchVoucherData(currentPage, searchValue, filters);
      } catch (e) {
        Toast.error(Message.Error.COMMON_MESSAGE);
      } finally {
        setPageLoading(false);
      }
    },
    [currentPage, filters, fetchVoucherData, searchValue]
  );

  const handleCancelBulkVoucher = useCallback(() => {
    const selectedRows = filteredData?.filter((item) => item?.checked) || [];
    if (!selectedRows.length) {
      return;
    }
    // A package refunds its whole price in one credit, so a cancel that
    // includes one is confirmed first (D-15); voucher-only goes straight through.
    if (selectedRows.some(isPackageRow)) {
      confirmAlert({
        title: '',
        message: Message.Package.CANCEL_CONFIRM,
        buttons: [
          { label: 'Yes', onClick: () => sendBulkCancel(selectedRows) },
          { label: 'No' },
        ],
        overlayClassName: 'overlay-custom-class-name',
      });
      return;
    }
    sendBulkCancel(selectedRows);
  }, [filteredData, sendBulkCancel]);

  const handleCloseFailures = () => setFailedRows([]);

  function handleCheckboxChange(row, value) {
    if (row === 'all') {
      const updatedData = filteredData?.map((item) => ({
        ...item,
        checked: value,
      }));
      setFilteredData(updatedData);
    } else {
      const updatedData = filteredData?.map((item) =>
        // Keyed on the server-supplied rowKey, never on id: a package row has
        // no id, so an id comparison would match every package row at once.
        row?.rowKey && item?.rowKey === row.rowKey
          ? { ...item, checked: value }
          : item
      );
      setFilteredData(updatedData);
    }
  }

  return {
    pageLoading,
    dataPerPage,
    totalNumber,
    pageCount,
    navigate,
    sectionObj,
    filteredData,
    fetchVoucherData,
    handleCheckboxChange,
    bottomButtons,
    setCurrentPage,
    currentPage,
    handleChange,
    values,
    handleSubmit,
    failedRows,
    isFailureOpen: failedRows.length > 0,
    handleCloseFailures,
  };
};

export default useCancelVoucher;